import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './money';
import { AdminUserReferralSchema } from './referral';
import { AdminUserResellerSchema } from './reseller';
import { RESULT_URL_MODES, TASK_STATUSES } from './tasks';

/** 新文件可写入的存储后端（阿里云 OSS 已停用写入，只读历史文件） */
export const STORAGE_BACKENDS = ['r2', 'tos'] as const;
export type StorageBackend = (typeof STORAGE_BACKENDS)[number];

export const ADMIN_ROLES = ['operator', 'sysadmin'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ADMIN_PERMISSIONS = [
  'dashboard.read',
  'user.read',
  'user.write',
  'quota.write',
  'model.write',
  'price.write',
  'channel.read',
  'channel.write',
  'channel.credential.write',
  'asset.read',
  'asset.raw.view',
  'task.read',
  'task.operate',
  'alert.write',
  'audit.read',
  'admin.manage',
  'settings.write',
] as const;
export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

export const AdminLoginSchema = z.object({ email: z.email(), password: z.string().min(1) });
export const AdminLoginResultSchema = z.object({
  mfa_required: z.literal(true),
  /** 首次登录未绑定 TOTP 时返回绑定信息 */
  mfa_setup: z.object({ otpauth_url: z.string(), secret: z.string() }).nullable(),
});
export const AdminMfaSchema = z.object({ code: z.string().regex(/^\d{6}$/) });

export const AdminMeSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(ADMIN_ROLES),
  permissions: z.array(z.enum(ADMIN_PERMISSIONS)),
  csrf_token: z.string(),
  site_id: z.enum(['cn', 'global']),
  site_name: z.string(),
  environment: z.string(),
  currency: CurrencySchema,
});
export type AdminMe = z.infer<typeof AdminMeSchema>;

export const DashboardSchema = z.object({
  currency: CurrencySchema,
  today: z.object({ revenue: z.string(), upstream_cost: z.string(), tasks: z.number() }),
  last7d: z.object({ revenue: z.string(), upstream_cost: z.string(), tasks: z.number() }),
  rates: z.object({
    first_attempt_success: z.number().nullable(),
    final_success: z.number().nullable(),
    fallback_success: z.number().nullable(),
    avg_recovery_seconds: z.number().nullable(),
  }),
  reserved_total: z.string(),
  open_alerts: z.number(),
  failure_breakdown: z.array(z.object({ code: z.string(), count: z.number() })),
  channels: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      status: z.string(),
      health_state: z.string(),
      in_flight: z.number(),
      concurrency_limit: z.number(),
      error_rate_1h: z.number().nullable(),
    }),
  ),
  daily: z.array(z.object({ day: z.string(), revenue: z.string(), tasks: z.number(), failed: z.number() })),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

export const AdminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  email_verified: z.boolean(),
  status: z.enum(['active', 'suspended', 'closed']),
  available: z.string(),
  reserved: z.string(),
  spend_30d: z.string(),
  created_at: z.string(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserDetailSchema = AdminUserSchema.extend({
  granted_total: z.string(),
  settled_total: z.string(),
  rpm_limit: z.number().nullable(),
  concurrency_limit: z.number().nullable(),
  /** 是否开启了两步验证（后台可在用户丢失验证器时重置） */
  mfa_enabled: z.boolean(),
  /** 存储后端与视频地址给法（null = 跟随全站默认） */
  storage_backend: z.enum(STORAGE_BACKENDS).nullable(),
  result_url_mode: z.enum(RESULT_URL_MODES).nullable(),
  storage_quota_bytes: z.number(),
  storage_used_bytes: z.number(),
  suspend_reason: z.string().nullable(),
  model_grants: z.array(z.string()),
  api_keys: z.array(
    z.object({ id: z.string(), name: z.string(), display: z.string(), status: z.string(), used: z.string() }),
  ),
  /** 推荐关系与分润汇总（docs/11） */
  referral: AdminUserReferralSchema,
  /** 分销渠道归属与渠道后台成员身份（docs/12） */
  reseller: AdminUserResellerSchema,
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

export const UpdateUserLimitsSchema = z.object({
  rpm_limit: z.number().int().positive().nullable().optional(),
  concurrency_limit: z.number().int().positive().nullable().optional(),
  storage_quota_bytes: z.number().int().nonnegative().optional(),
  storage_backend: z.enum(STORAGE_BACKENDS).nullable().optional(),
  result_url_mode: z.enum(RESULT_URL_MODES).nullable().optional(),
});
export const SuspendUserSchema = z.object({ reason: z.string().min(1).max(200), allow_result_download: z.boolean() });
export const ModelGrantsSchema = z.object({ models: z.array(z.string()) });

/** 上游余额（自动查询）：scope=key 是渠道 Key 的剩余额度，account 是账户余额；method=unsupported 表示该上游没有余额接口 */
export const UPSTREAM_BALANCE_METHODS = ['ark_billing', 'newapi_token', 'newapi_user', 'unsupported'] as const;
export const UpstreamBalanceSchema = z.object({
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  scope: z.enum(['key', 'account']).nullable(),
  method: z.enum(UPSTREAM_BALANCE_METHODS),
  checked_at: z.string(),
  error: z.string().nullable(),
});
export type UpstreamBalance = z.infer<typeof UpstreamBalanceSchema>;

export const ProviderAccountSchema = z.object({
  id: z.string(),
  provider: z.enum(['volcengine_ark', 'byteplus_modelark', 'modelland', 'mock']),
  label: z.string(),
  fault_domain: z.string(),
  shared_rpm_limit: z.number(),
  shared_concurrency_limit: z.number(),
  balance_note: z.string().nullable(),
  balance_updated_at: z.string().nullable(),
  balance: UpstreamBalanceSchema.nullable(),
  balance_threshold: z.string().nullable(),
  balance_token_set: z.boolean(),
  balance_user_id: z.string().nullable(),
  entitlement_expires_at: z.string().nullable(),
  status: z.string(),
  projects: z.array(
    z.object({ id: z.string(), project_name: z.string(), asset_credential_fingerprint: z.string().nullable(), asset_mode: z.enum(['relay']).nullable().default(null) }),
  ),
});
export type ProviderAccount = z.infer<typeof ProviderAccountSchema>;

export const UpsertProviderAccountSchema = z.object({
  provider: z.enum(['volcengine_ark', 'byteplus_modelark', 'modelland', 'mock']),
  label: z.string().min(1).max(64),
  fault_domain: z.string().min(1).max(64),
  shared_rpm_limit: z.number().int().positive(),
  shared_concurrency_limit: z.number().int().positive(),
  balance_note: z.string().max(200).nullable().optional(),
  /** 余额低于这个金额时告警并推送 Telegram；null 不预警 */
  balance_threshold: z.string().regex(/^\d{1,12}(\.\d{1,6})?$/).nullable().optional(),
  /** new-api 系统访问令牌（个人设置 → 生成令牌）；null 清除；不传保持不变 */
  balance_token: z.string().min(8).max(256).nullable().optional(),
  balance_user_id: z.string().regex(/^\d{1,12}$/).nullable().optional(),
  entitlement_expires_at: z.iso.datetime().nullable().optional(),
});
export const CreateProviderProjectSchema = z.object({
  provider_account_id: z.string(),
  project_name: z.string().min(1).max(64),
});

export const ChannelSchema = z.object({
  id: z.string(),
  code: z.string(),
  provider_account_id: z.string(),
  provider_account_label: z.string(),
  provider_project_id: z.string(),
  project_name: z.string(),
  base_url: z.string(),
  priority: z.number(),
  rpm_limit: z.number(),
  concurrency_limit: z.number(),
  in_flight: z.number(),
  status: z.enum(['active', 'draining', 'disabled']),
  health_state: z.enum(['healthy', 'cooling', 'half_open', 'isolated']),
  consecutive_failures: z.number(),
  cooldown_until: z.string().nullable(),
  credential_fingerprint: z.string().nullable(),
  models: z.array(z.string()),
  hot_replica: z.boolean(),
  last_check: z.unknown().nullable(),
  version: z.number(),
});
export type Channel = z.infer<typeof ChannelSchema>;

export const CreateChannelSchema = z.object({
  code: z.string().regex(/^[A-Z0-9-]{2,16}$/),
  provider_project_id: z.string(),
  base_url: z.url(),
  priority: z.number().int().min(0).max(1000),
  rpm_limit: z.number().int().positive(),
  concurrency_limit: z.number().int().positive(),
  models: z.array(z.string()),
  hot_replica: z.boolean().default(false),
  inference_key: z.string().min(1).optional(),
});
export const UpdateChannelSchema = z.object({
  priority: z.number().int().min(0).max(1000).optional(),
  rpm_limit: z.number().int().positive().optional(),
  concurrency_limit: z.number().int().positive().optional(),
  models: z.array(z.string()).optional(),
  hot_replica: z.boolean().optional(),
  base_url: z.url().optional(),
  version: z.number().int(),
});
export const ChannelCredentialSchema = z.object({ inference_key: z.string().min(1) });
export const ReorderChannelsSchema = z.object({ ordered_ids: z.array(z.string()).min(1) });

export const RoutingPreviewSchema = z.object({
  model: z.string(),
  ordered: z.array(z.object({ channel_id: z.string(), code: z.string(), priority: z.number(), note: z.string() })),
  excluded: z.array(z.object({ channel_id: z.string(), code: z.string(), reason: z.string() })),
});
export type RoutingPreview = z.infer<typeof RoutingPreviewSchema>;

export const AdminModelSchema = z.object({
  id: z.string(),
  public_name: z.string(),
  display_name: z.string(),
  provider: z.string(),
  upstream_model_id: z.string(),
  status: z.enum(['draft', 'verified', 'listed', 'delisted']),
  capabilities: z.unknown(),
  current_price: z
    .object({ id: z.string(), effective_from: z.string(), rules: z.unknown(), currency: CurrencySchema })
    .nullable(),
  price_history: z.array(z.object({ id: z.string(), effective_from: z.string(), rules: z.unknown() })),
  channel_count: z.number(),
});
export type AdminModel = z.infer<typeof AdminModelSchema>;

export const CreatePriceVersionSchema = z.object({
  model_version_id: z.string(),
  effective_from: z.iso.datetime(),
  rules: z.unknown(),
});

export const AttemptSchema = z.object({
  id: z.string(),
  seq: z.number(),
  channel_code: z.string(),
  provider_account_label: z.string(),
  /** 本次尝试使用的模型（跨模型兜底后与请求的模型不同） */
  model: z.string().nullable(),
  status: z.string(),
  upstream_task_id: z.string().nullable(),
  error_class: z.string().nullable(),
  error_message: z.string().nullable(),
  route_reason: z.unknown(),
  cost: z.string().nullable(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
});
export type Attempt = z.infer<typeof AttemptSchema>;

export const AdminTaskSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_email: z.string(),
  model: z.string(),
  status: z.enum(TASK_STATUSES),
  failure_code: z.string().nullable(),
  attempts: z.number(),
  reserved: z.string(),
  charged: z.string().nullable(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
});
export type AdminTask = z.infer<typeof AdminTaskSchema>;

export const AdminTaskDetailSchema = AdminTaskSchema.extend({
  /** 客户请求的模型；model 为实际（当前）模型，二者不同说明发生了跨模型兜底 */
  requested_model: z.string(),
  /** 剩余未用的兜底模型 */
  fallback_models: z.array(z.string()),
  request_id: z.string(),
  deadline_at: z.string(),
  params: z.unknown(),
  usage: z.unknown(),
  upstream_cost: z.string().nullable(),
  attempt_list: z.array(AttemptSchema),
  events: z.array(z.object({ from: z.string().nullable(), to: z.string(), reason: z.string().nullable(), at: z.string() })),
  webhook_deliveries: z.array(
    z.object({ attempt_no: z.number(), status_code: z.number().nullable(), error: z.string().nullable(), at: z.string() }),
  ),
  ledger: z.array(z.object({ type: z.string(), amount: z.string(), at: z.string() })),
});
export type AdminTaskDetail = z.infer<typeof AdminTaskDetailSchema>;

export const AlertSchema = z.object({
  id: z.string(),
  type: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  title: z.string(),
  detail: z.string().nullable(),
  target: z.string().nullable(),
  status: z.enum(['open', 'acked', 'resolved']),
  occurrences: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Alert = z.infer<typeof AlertSchema>;

export const AuditEventSchema = z.object({
  id: z.string(),
  actor: z.string(),
  action: z.string(),
  target_type: z.string(),
  target_id: z.string().nullable(),
  reason: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
  ip: z.string().nullable(),
  created_at: z.string(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const AdminAccountSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(ADMIN_ROLES),
  status: z.string(),
  mfa_enabled: z.boolean(),
  last_login_at: z.string().nullable(),
});
export const CreateAdminSchema = z.object({
  email: z.email(),
  password: z.string().min(12),
  role: z.enum(ADMIN_ROLES),
});

export const SiteSettingsSchema = z.object({
  announcement: z.string().max(500).nullable(),
  contact_email: z.string().max(200).nullable(),
  contact_wechat: z.string().max(100).nullable(),
  contact_note: z.string().max(300).nullable(),
  default_rpm_limit: z.number().int().positive(),
  default_concurrency_limit: z.number().int().positive(),
  default_storage_quota_bytes: z.number().int().nonnegative(),
  task_deadline_minutes: z.number().int().min(5).max(120),
  max_submit_attempts: z.number().int().min(1).max(8),
  result_retention_days: z.number().int().min(1).max(90),
  /** 新文件默认存储后端（R2 / 火山 TOS）；可按用户覆盖 */
  default_storage_backend: z.enum(STORAGE_BACKENDS).default('r2'),
  /** 视频地址给法；可按用户覆盖 */
  result_url_mode: z.enum(RESULT_URL_MODES).default('platform'),
  /** 注册时自动授权的模型（public_name）；空 = 注册后由后台逐个授权 */
  signup_model_grants: z.array(z.string().min(1).max(64)).max(50).default([]),
});
export type SiteSettings = z.infer<typeof SiteSettingsSchema>;

/** 首页成功率展示（docs/15）：按模型统计近 N 天成功率，样本少于 min_samples 时显示 fallback_rate */
export const HomeStatsSettingsSchema = z.object({
  window_days: z.number().int().min(1).max(90).default(30),
  min_samples: z.number().int().min(1).max(100_000).default(200),
  models: z
    .array(
      z.object({
        model: z.string().min(1),
        enabled: z.boolean(),
        /** 样本不足时显示的成功率（百分比，如 99.9） */
        fallback_rate: z.number().min(0).max(100),
      }),
    )
    .max(20)
    .default([]),
});
export type HomeStatsSettings = z.infer<typeof HomeStatsSettingsSchema>;

export { MoneyStringSchema };
export type AdminAccount = z.infer<typeof AdminAccountSchema>;

// ───────────── 系统健康（后台“系统健康”页） ─────────────

export const HEALTH_LEVELS = ['ok', 'warn', 'crit'] as const;
export type HealthLevel = (typeof HEALTH_LEVELS)[number];

export interface HealthFinding {
  /** 稳定标识（不含会变化的数字），用于告警去重、持续时间判断与恢复通知 */
  code: string;
  level: Exclude<HealthLevel, 'ok'>;
  area: 'database' | 'queue' | 'instances' | 'business';
  title: string;
  detail: string;
  /** 处理建议（扩容、升级、排查方向） */
  advice: string;
}

export interface InstanceHealth {
  instance_id: string;
  role: 'api' | 'worker';
  version: string | null;
  started_at: string;
  last_seen_at: string;
  stale: boolean;
  cpu_percent: number | null;
  cpu_limit_cores: number | null;
  memory_bytes: number;
  memory_limit_bytes: number | null;
  heap_used_bytes: number;
  event_loop_p99_ms: number | null;
  pool: { max: number; total: number; idle: number; waiting: number } | null;
  node_version: string;
}

/** 阿里云云监控的一项使用率指标（百分比） */
export interface CloudMetric {
  key: 'cpu' | 'memory' | 'disk' | 'iops' | 'connections';
  label: string;
  latest: number | null;
  max_1h: number | null;
  warn: number;
  crit: number;
  series: { t: string; v: number }[];
}

export interface SystemHealth {
  checked_at: string;
  level: HealthLevel;
  findings: HealthFinding[];
  infra: { tier: string | null; db_class: string | null; db_storage_gb: number | null };
  database: {
    reachable: boolean;
    latency_ms: number | null;
    version: string | null;
    started_at: string | null;
    connections: { total: number; max: number; active: number; idle: number; idle_in_transaction: number; waiting_on_lock: number };
    /** 本站数据库大小 */
    size_bytes: number;
    /** 实例上可见的全部数据库大小（staging 与 production 共用一个实例） */
    instance_size_bytes: number;
    cache_hit_ratio: number | null;
    deadlocks: number;
    xid_age_ratio: number;
    long_queries: { pid: number; seconds: number; state: string; query: string }[];
    largest_tables: { name: string; bytes: number; rows: number; dead_ratio: number }[];
  };
  queue: {
    ready: number;
    scheduled: number;
    running: number;
    failed: number;
    oldest_ready_seconds: number | null;
    by_task: { task: string; ready: number; running: number; failed: number }[];
    crons: { name: string; last_run_at: string | null; overdue: boolean }[];
  };
  instances: InstanceHealth[];
  /** RDS 云监控：未配置实例 ID 时 rds 为 null；调用失败时 error 有值 */
  cloud: { rds: { instance_id: string; metrics: CloudMetric[] } | null; error: string | null };
  business: {
    tasks_in_flight: number;
    tasks_stuck: number;
    last_hour: { succeeded: number; failed: number };
    channels: { active: number; unhealthy: number; disabled: number };
  };
}

/** 发布流水线（docs/15 §11）：函数计算 release-trigger 的 /status */
export interface ReleaseRun {
  id: string;
  /** 运行中 / 成功 / 失败（取不到时为说明文字） */
  state: string;
  started: string;
  /** 已结束的任务的用时（如“4 分 12 秒”），运行中为空 */
  duration?: string;
  message: string;
}

export interface ReleaseStatus {
  /** 本环境没有配置发布页地址（RELEASE_CONSOLE_URL）时为 false，其余字段为空 */
  configured: boolean;
  /** staging / production 的 api 正在运行的镜像标签（= 提交号） */
  staging: string;
  production: string;
  /** 国内站正在运行的标签；没部署国内站时没有这个字段 */
  cn?: string;
  /** 最新构建好的发布镜像标签 */
  latest: string;
  runs: { staging: ReleaseRun[]; production: ReleaseRun[]; cn?: ReleaseRun[] };
}

export interface ReleaseActionResult {
  env: 'staging' | 'production';
  tag: string;
  notice: string;
}
